/* ==========================================================================
   Caprahem Theme — Global JS
   ========================================================================== */

(function () {
  'use strict';

  // ----- Utilities -----
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const $ = (sel, root) => (root || document).querySelector(sel);

  function trapFocus(container, focusEl) {
    const focusables = container.querySelectorAll('a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])');
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    (focusEl || first || container).focus({ preventScroll: true });
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last && last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first && first.focus();
      }
    }
    container.__trap = handler;
    container.addEventListener('keydown', handler);
  }
  function releaseFocus(container) {
    if (container && container.__trap) {
      container.removeEventListener('keydown', container.__trap);
      container.__trap = null;
    }
  }

  // ----- Drawer / Modal Manager -----
  const Drawer = {
    open(id, opener) {
      const el = document.getElementById(id);
      if (!el) return;
      // Close any other open drawer first (e.g. mobile menu -> wishlist)
      const current = document.querySelector('[data-drawer].is-open');
      if (current && current.id !== id) this.close(current.id);
      el.hidden = false;
      el.setAttribute('open', '');
      requestAnimationFrame(() => el.classList.add('is-open'));
      document.body.classList.add('is-drawer-open');
      el.dataset.opener = opener && opener.id ? opener.id : '';
      const focusable = el.querySelector('[data-drawer-focus]') || el.querySelector('button, a, input');
      trapFocus(el, focusable);
      el.addEventListener('keydown', escHandler);
    },
    close(id) {
      const el = document.getElementById(id);
      if (!el) return;
      el.classList.remove('is-open');
      releaseFocus(el);
      el.removeEventListener('keydown', escHandler);
      setTimeout(() => {
        el.hidden = true;
        el.removeAttribute('open');
      }, 350);
      document.body.classList.remove('is-drawer-open');
      const openerId = el.dataset.opener;
      if (openerId) {
        const opener = document.getElementById(openerId);
        if (opener) opener.focus();
      }
    },
  };
  function escHandler(e) {
    if (e.key === 'Escape') {
      const open = document.querySelector('[data-drawer].is-open');
      if (open) Drawer.close(open.id);
    }
  }
  window.CaprahemDrawer = Drawer;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      const drawerId = opener.dataset.drawerOpen;
      Drawer.open(drawerId, opener);
      if (drawerId === 'wishlist-drawer' && window.CaprahemWishlistRender) {
        window.CaprahemWishlistRender();
      }
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      const drawer = closer.closest('[data-drawer]');
      if (drawer) Drawer.close(drawer.id);
      return;
    }
    const overlay = e.target.closest('[data-drawer-overlay]');
    if (overlay) {
      const drawer = overlay.closest('[data-drawer]');
      if (drawer) Drawer.close(drawer.id);
    }
  });

  // ----- Accordion (using <details>) -----
  // Native <details> handles open/close. Add smooth animation for content reveal.
  $$('details.accordion').forEach((d) => {
    d.addEventListener('toggle', () => {
      // Optionally close siblings — disabled by default
    });
  });

  // Viewers who asked their OS to reduce motion get instant transitions instead of
  // animated ones, and no auto-rotating content. Re-read live so a preference change
  // takes effect without a reload.
  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ----- Header sticky shadow -----
  const header = $('[data-sticky-header]');
  if (header) {
    // Scroll fires far more often than the class actually needs to change. Coalesce
    // into one rAF per frame and only touch the DOM on an actual state change, so
    // scrolling never triggers a style recalc it did not need.
    let scrolled = false;
    let ticking = false;
    const syncHeader = () => {
      ticking = false;
      const next = window.scrollY > 4;
      if (next === scrolled) return;
      scrolled = next;
      header.classList.toggle('is-scrolled', next);
    };
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(syncHeader);
    }, { passive: true });
    syncHeader();
  }

  // ----- Quantity widget -----
  document.addEventListener('click', (e) => {
    const minus = e.target.closest('[data-qty-minus]');
    const plus = e.target.closest('[data-qty-plus]');
    if (minus || plus) {
      const wrap = (minus || plus).closest('.quantity');
      if (!wrap) return;
      const input = wrap.querySelector('input[type="number"]');
      if (!input || input.disabled) return;
      const min = parseInt(input.min || '1', 10);
      const max = parseInt(input.max || '999', 10);
      const current = parseInt(input.value || String(min), 10);
      let val = minus ? current - 1 : current + 1;
      val = Math.max(min, Math.min(max, val));
      if (val === current) return; // already at the limit — no needless update/reload
      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  // ----- Cart (fetch-based) -----
  const Cart = {
    async getState() {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return res.json();
    },
    async addItem(formData) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart.');
      }
      return res.json();
    },
    async updateItem(line, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ line, quantity }),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      // Shopify answers with 422 + {message, description} when the requested quantity
      // exceeds available stock. Returning that body as if it were a cart is why the
      // field used to snap back with no explanation.
      if (!res.ok) {
        throw new Error(data.description || data.message || 'Could not update the cart.');
      }
      return data;
    },
    async refreshDrawer() {
      try {
        const url = window.location.pathname + (window.location.pathname.includes('?') ? '&' : '?') + 'sections=cart-drawer';
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        const data = await res.json();
        const html = data && data['cart-drawer'];
        if (html) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          // Section Rendering API wraps output in <div id="shopify-section-…">; find the inner drawer.
          const newDrawer = doc.querySelector('[data-drawer="cart"]');
          const cur = document.getElementById('cart-drawer');
          if (newDrawer && cur) {
            const newPanel = newDrawer.querySelector('[data-cart-drawer-panel]');
            const curPanel = cur.querySelector('[data-cart-drawer-panel]');
            if (newPanel && curPanel) {
              curPanel.innerHTML = newPanel.innerHTML;
            } else {
              // Fallback: replace whole drawer content (preserves the open state class on cur)
              cur.innerHTML = newDrawer.innerHTML;
            }
          }
        }
      } catch (e) { /* graceful fail */ }
      this.refreshCount();
    },
    async refreshCount() {
      try {
        const state = await this.getState();
        $$('[data-cart-count]').forEach((el) => {
          el.textContent = state.item_count;
          el.toggleAttribute('hidden', state.item_count === 0);
        });
      } catch (e) {}
    },
    // Re-render the full cart page section in place (no full-page reload)
    async refreshPage() {
      const el = document.querySelector('[data-cart-page]');
      if (!el) { window.location.reload(); return; }
      const sectionId = el.dataset.sectionId;
      try {
        const res = await fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sectionId), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok) throw new Error('cart section fetch failed');
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.getElementById('shopify-section-' + sectionId);
        const wrapper = document.getElementById('shopify-section-' + sectionId);
        if (fresh && wrapper) {
          wrapper.innerHTML = fresh.innerHTML;
          this.refreshCount();
        } else {
          window.location.reload();
        }
      } catch (e) {
        window.location.reload();
      }
    },
  };
  window.CaprahemCart = Cart;

  // Add-to-cart form (intercept on product page when drawer cart is enabled)
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    if (form.dataset.cartType !== 'drawer') return;
    e.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (button) { button.disabled = true; button.classList.add('is-loading'); }
    try {
      await Cart.addItem(new FormData(form));
      await Cart.refreshDrawer();
      Drawer.open('cart-drawer');
    } catch (err) {
      const errEl = form.querySelector('[data-form-error]');
      if (errEl) errEl.textContent = err.message;
    } finally {
      if (button) { button.disabled = false; button.classList.remove('is-loading'); }
    }
  });

  // ----- Buy it now (add to cart, then straight to checkout) -----
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-buy-now]');
    if (!btn || btn.disabled) return;
    const form = btn.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const errEl = form.querySelector('[data-form-error]');
    if (errEl) errEl.textContent = '';
    btn.disabled = true; btn.classList.add('is-loading');
    try {
      await Cart.addItem(new FormData(form));
      window.location.href = '/checkout';
    } catch (err) {
      btn.disabled = false; btn.classList.remove('is-loading');
      if (errEl) errEl.textContent = err.message;
    }
  });

  // Cart item update — drawer or full cart page (debounced so rapid +/- clicks coalesce)
  const cartQtyTimers = {};
  document.addEventListener('change', (e) => {
    const qty = e.target.closest('[data-cart-qty-input]');
    if (!qty) return;
    const line = parseInt(qty.dataset.lineIndex, 10);
    const min = parseInt(qty.min || '1', 10);
    let value = parseInt(qty.value, 10);
    if (isNaN(value) || value < min) value = min;
    qty.value = value; // keep the field in sync with the clamped value
    const inDrawer = !!qty.closest('#cart-drawer');
    if (qty.disabled) return;
    clearTimeout(cartQtyTimers[line]);
    cartQtyTimers[line] = setTimeout(async () => {
      qty.disabled = true;
      try {
        await Cart.updateItem(line, value);
        setQtyError(qty, '');
        if (inDrawer) { await Cart.refreshDrawer(); }
        else { await Cart.refreshPage(); }
      } catch (err) {
        qty.disabled = false;
        setQtyError(qty, err.message);
        // Put the field back to what the cart actually holds, so the number on screen
        // is never a quantity the shopper cannot buy.
        try {
          const state = await Cart.getState();
          const item = state.items[line - 1];
          if (item) {
            qty.value = item.quantity;
            if (item.quantity > 0) qty.max = item.quantity;
          }
        } catch (e) { /* leave the field as-is */ }
      }
    }, 350);
  });

  // Inline message under a cart quantity field. Passing an empty message clears it.
  function setQtyError(input, message) {
    const wrap = input.closest('.quantity') || input.parentElement;
    if (!wrap) return;
    let el = wrap.parentElement && wrap.parentElement.querySelector('[data-qty-error]');
    if (!message) { if (el) el.remove(); return; }
    if (!el) {
      el = document.createElement('p');
      el.className = 'qty-error';
      el.setAttribute('data-qty-error', '');
      el.setAttribute('role', 'status');
      wrap.insertAdjacentElement('afterend', el);
    }
    el.textContent = message;
  }

  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (!remove) return;
    e.preventDefault();
    // "Are you sure?" confirmation before removing
    const confirmMsg = remove.dataset.confirm;
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    const line = parseInt(remove.dataset.lineIndex, 10);
    await Cart.updateItem(line, 0);
    if (remove.closest('#cart-drawer')) {
      await Cart.refreshDrawer();
    } else {
      await Cart.refreshPage();
    }
  });

  // Discount code / coupon — apply via the /discount route, then land back on the cart
  document.addEventListener('click', (e) => {
    const apply = e.target.closest('[data-discount-apply]');
    if (!apply) return;
    e.preventDefault();
    const wrap = apply.closest('[data-discount-form]');
    const input = wrap && wrap.querySelector('[data-discount-input]');
    const code = input && input.value.trim();
    if (!code) { if (input) input.focus(); return; }
    apply.disabled = true;
    window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/cart';
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const input = e.target.closest('[data-discount-input]');
    if (!input) return;
    e.preventDefault();
    const wrap = input.closest('[data-discount-form]');
    const btn = wrap && wrap.querySelector('[data-discount-apply]');
    if (btn) btn.click();
  });

  // ----- Predictive search -----
  const searchInput = $('[data-predictive-input]');
  const searchResults = $('[data-predictive-results]');
  const searchForm = searchInput && searchInput.closest('form[data-search-form]');
  const searchScope = searchForm && searchForm.querySelector('[data-search-scope]');
  const searchTypeEl = searchForm && searchForm.querySelector('[data-search-type]');

  function applyScope(value) {
    if (!searchForm || !searchTypeEl) return;
    let existingFields = searchForm.querySelector('input[name="options[fields]"]');
    if (existingFields) existingFields.remove();
    if (value === 'name') {
      searchTypeEl.value = 'product';
      const fields = document.createElement('input');
      fields.type = 'hidden';
      fields.name = 'options[fields]';
      fields.value = 'title';
      searchForm.appendChild(fields);
    } else if (value === 'collection') {
      searchTypeEl.value = 'collection';
    } else {
      searchTypeEl.value = 'product,collection,article,page';
    }
  }

  function getResourceTypes() {
    if (!searchScope) return 'product,collection,article,page';
    if (searchScope.value === 'name') return 'product';
    if (searchScope.value === 'collection') return 'collection';
    return 'product,collection,article,page';
  }

  let predictiveAbort;
  async function runPredictive(q) {
    // Drop the in-flight request before starting another. Without this a slow
    // earlier response can land after a newer one and overwrite fresher results,
    // and the browser holds open connections nobody is waiting on.
    if (predictiveAbort) predictiveAbort.abort();
    predictiveAbort = new AbortController();
    const signal = predictiveAbort.signal;
    try {
      const types = getResourceTypes();
      const fieldsParam = searchScope && searchScope.value === 'name' ? '&resources[options][fields]=title' : '';
      const res = await fetch(`/search/suggest?q=${encodeURIComponent(q)}&resources[type]=${types}&resources[limit]=6${fieldsParam}&section_id=predictive-search`, { signal });
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const inner = doc.querySelector('[data-predictive-content]');
      searchResults.innerHTML = inner ? inner.innerHTML : '';
      searchResults.classList.add('is-open');
    } catch (e) { /* aborted or offline — leave the previous results in place */ }
  }

  let searchTimer;
  if (searchInput && searchResults) {
    searchInput.addEventListener('input', () => {
      const q = searchInput.value.trim();
      clearTimeout(searchTimer);
      if (!q) { searchResults.classList.remove('is-open'); searchResults.innerHTML = ''; return; }
      searchTimer = setTimeout(() => runPredictive(q), 200);
    });
    if (searchScope) {
      searchScope.addEventListener('change', () => {
        applyScope(searchScope.value);
        const q = searchInput.value.trim();
        if (q) runPredictive(q);
      });
    }
    document.addEventListener('click', (e) => {
      if (!searchResults.contains(e.target) && !searchInput.contains(e.target)) {
        searchResults.classList.remove('is-open');
      }
    });
  }

  // ----- Product gallery carousel -----
  const ProductGallery = {
    init(el) {
      const viewport = $('[data-gallery-viewport]', el);
      if (!viewport) return null;
      const dotsWrap = $('[data-gallery-dots]', el);
      const counterEl = $('[data-gallery-counter]', el);
      const inst = {
        el, viewport, dotsWrap,
        allSlides: $$('[data-slide]', viewport),
        visible: [],
        current: 0,
      };

      const scrollLeftFor = (slide) => slide.offsetLeft - viewport.offsetLeft;

      function setActive(i) {
        inst.current = i;
        // Mobile shows "3 / 20" in place of the dots — see .product__gallery-counter
        if (counterEl) {
          counterEl.textContent = inst.visible.length ? (i + 1) + ' / ' + inst.visible.length : '';
        }
        if (!dotsWrap) return;
        $$('.product__gallery-dot', dotsWrap).forEach((d, idx) => {
          d.classList.toggle('is-active', idx === i);
          d.setAttribute('aria-selected', idx === i ? 'true' : 'false');
        });
      }

      function buildDots() {
        if (!dotsWrap) return;
        dotsWrap.innerHTML = '';
        inst.visible.forEach((s, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'product__gallery-dot' + (i === inst.current ? ' is-active' : '');
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-label', 'Go to image ' + (i + 1));
          dot.addEventListener('click', () => inst.go(i));
          dotsWrap.appendChild(dot);
        });
      }

      inst.go = function (i, smooth) {
        if (!inst.visible.length) return;
        i = Math.max(0, Math.min(i, inst.visible.length - 1));
        const instant = smooth === false || prefersReducedMotion();
        // The viewport carries scroll-behavior: smooth in CSS, and behavior: 'auto'
        // defers to that — so an instant jump has to switch the CSS off for the one
        // call, or picking a colour visibly races through every image in between.
        const prevBehavior = viewport.style.scrollBehavior;
        if (instant) viewport.style.scrollBehavior = 'auto';
        viewport.scrollTo({ left: scrollLeftFor(inst.visible[i]), behavior: instant ? 'instant' : 'smooth' });
        if (instant) {
          // Restore on the next frame, once the jump has been applied.
          window.requestAnimationFrame(() => { viewport.style.scrollBehavior = prevBehavior; });
        }
        setActive(i);
      };
      // Kept for the dots and for any caller that steps through the gallery; the
      // on-image arrows were removed.
      inst.prev = () => inst.go(inst.current - 1);
      inst.next = () => inst.go(inst.current + 1);

      inst.refresh = function () {
        inst.visible = inst.allSlides.filter((s) => s.style.display !== 'none');
        inst.current = 0;
        buildDots();
        inst.go(0, false);
        setActive(0);
        const single = inst.visible.length <= 1;
        if (dotsWrap) dotsWrap.style.display = single ? 'none' : '';
        if (counterEl) counterEl.style.display = single ? 'none' : '';
      };

      const colorsOf = (s) => (s.dataset.mediaColors || '').split('||').filter(Boolean);

      inst.filterByColor = function (color) {
        if (!color) return;
        const hasTagged = inst.allSlides.some((s) => colorsOf(s).length > 0);
        if (!hasTagged) return;
        const matches = inst.allSlides.filter((s) => colorsOf(s).indexOf(color) !== -1);
        if (!matches.length) {
          inst.allSlides.forEach((s) => { s.style.display = ''; });
          inst.refresh();
          return;
        }
        const untagged = inst.allSlides.filter((s) => colorsOf(s).length === 0);
        const keepUntagged = matches.length === 1 && untagged.length <= 2;
        inst.allSlides.forEach((s) => {
          const colors = colorsOf(s);
          const show = colors.indexOf(color) !== -1 || (keepUntagged && colors.length === 0);
          s.style.display = show ? '' : 'none';
        });
        inst.refresh();
      };

      // Jump to a specific media item — used when a shopper picks a colour, so the
      // gallery lands on the image the merchant attached to that variant in the admin.
      inst.goToMedia = function (mediaId) {
        if (!mediaId) return false;
        const target = inst.allSlides.find((s) => s.dataset.mediaId === String(mediaId));
        if (!target) return false;
        // A colour filter may have hidden it; un-hide and re-index before scrolling.
        if (target.style.display === 'none') {
          target.style.display = '';
          inst.refresh();
        }
        const i = inst.visible.indexOf(target);
        if (i === -1) return false;
        inst.go(i, false);
        return true;
      };

      let scrollTimer;
      viewport.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          let nearest = 0, min = Infinity;
          inst.visible.forEach((s, i) => {
            const d = Math.abs(scrollLeftFor(s) - viewport.scrollLeft);
            if (d < min) { min = d; nearest = i; }
          });
          setActive(nearest);
        }, 90);
      }, { passive: true });

      inst.refresh();
      el.__gallery = inst;
      return inst;
    },
  };
  // No colour filtering on load. A plain product URL keeps the merchant's own media
  // order, and a ?variant= deep link is already filtered server-side in
  // main-product.liquid — so the gallery never reshuffles after first paint. Filtering
  // happens only when the shopper actually picks a colour (see the variant picker).
  // refresh() reads the rendered display state, so server-hidden slides are honoured.
  $$('[data-product-gallery]').forEach((el) => {
    ProductGallery.init(el);
  });

  // ----- Variant picker -----
  document.addEventListener('change', (e) => {
    const picker = e.target.closest('[data-variant-radios]');
    if (!picker) return;
    // The radios and the buy-button <form> are SEPARATE blocks (siblings, not nested),
    // so resolve the form via the shared .product container rather than as an ancestor.
    const productEl = picker.closest('.product') || document;
    const form = productEl.querySelector ? productEl.querySelector('form[data-product-form]') : null;
    const productId = picker.dataset.productId;
    const dataEl = document.getElementById('product-variants-' + productId);
    const selected = $$('input[type="radio"]:checked', picker).map((r) => r.value);
    // Move the visual "selected" outline to the option(s) the customer just picked.
    // Done first so the highlight always follows the click, even before variant matching.
    picker.querySelectorAll('.product__variant-option').forEach((lbl) => {
      const input = lbl.querySelector('input[type="radio"]');
      lbl.classList.toggle('is-selected', !!(input && input.checked));
    });
    // Update the "selected value" text shown next to each option name (e.g. "Color Black").
    picker.querySelectorAll('[data-variant-current]').forEach((el) => {
      const optIdx = parseInt(el.dataset.variantCurrent, 10);
      el.textContent = selected[optIdx] || '';
    });

    let variants = null;
    if (dataEl) { try { variants = JSON.parse(dataEl.textContent); } catch (e) { variants = null; } }
    const match = variants && variants.find((v) => v.options.every((opt, i) => opt === selected[i]));

    const galleryEl = $('[data-product-gallery]', productEl);
    if (galleryEl && galleryEl.__gallery) {
      const gallery = galleryEl.__gallery;
      // Narrow the gallery to the chosen colour first...
      if (galleryEl.dataset.colorIndex !== undefined) {
        const ci = parseInt(galleryEl.dataset.colorIndex, 10);
        if (!isNaN(ci) && selected[ci]) gallery.filterByColor(selected[ci]);
      }
      // ...then land on that variant's own image. Falls back to the filtered view
      // when the variant has no image assigned in the admin.
      if (match && match.featured_media && match.featured_media.id) {
        gallery.goToMedia(match.featured_media.id);
      }
    }

    if (!match) return;

    // Update form variant id
    if (form) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) idInput.value = match.id;
    }
    // Update price
    const priceEl = $('[data-product-price]', productEl);
    if (priceEl && match.price !== undefined) {
      priceEl.innerHTML = formatMoney(match.price, true);
      if (match.compare_at_price && match.compare_at_price > match.price) {
        priceEl.innerHTML = `<s>${formatMoney(match.compare_at_price, true)}</s> ${formatMoney(match.price, true)}`;
      }
    }
    // Update SKU
    const skuEl = $('[data-product-sku]', productEl);
    if (skuEl && match.sku) skuEl.textContent = match.sku;
    // Update availability
    const submit = form && form.querySelector('[type="submit"]');
    if (submit) {
      submit.disabled = !match.available;
      const label = submit.querySelector('[data-button-label]');
      if (label) label.textContent = match.available
        ? (label.dataset.defaultText || label.textContent)
        : (label.dataset.soldOutText || 'Sold out');
    }
    const buyNow = form && form.querySelector('[data-buy-now]');
    if (buyNow) buyNow.disabled = !match.available;
    // URL update
    if (history.replaceState && match.id) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      history.replaceState({}, '', url.toString());
    }
  });

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-variant-radios]') || e.target.closest('[data-product-gallery]')) return;
    const productEl = e.target.closest('.product');
    if (!productEl) return;
    const galleryEl = $('[data-product-gallery]', productEl);
    if (!galleryEl || !galleryEl.__gallery) return;
    const gallery = galleryEl.__gallery;
    const wasFiltered = gallery.allSlides.some((s) => s.style.display === 'none');
    if (!wasFiltered) return;
    gallery.allSlides.forEach((s) => { s.style.display = ''; });
    gallery.refresh();
  });

  // Render cents using the shop's own money format string rather than a hardcoded
  // symbol. Supports the five placeholders Shopify money formats can use.
  function formatMoney(cents, withCurrency) {
    const fmt = (withCurrency
      ? (window.theme && window.theme.moneyWithCurrencyFormat)
      : (window.theme && window.theme.moneyFormat)) || '{{amount}}';

    const group = (num, thousands, decimal, places) => {
      const fixed = (Math.abs(Number(cents)) / 100).toFixed(places);
      const parts = fixed.split('.');
      const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return places && parts[1] ? int + decimal + parts[1] : int;
    };

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => {
      switch (name) {
        case 'amount':                                  return group(cents, ',', '.', 2);
        case 'amount_no_decimals':                       return group(cents, ',', '.', 0);
        case 'amount_with_comma_separator':              return group(cents, '.', ',', 2);
        case 'amount_no_decimals_with_comma_separator':  return group(cents, '.', ',', 0);
        case 'amount_with_apostrophe_separator':         return group(cents, "'", '.', 2);
        default:                                         return group(cents, ',', '.', 2);
      }
    });
  }

  // ----- Read more (product description) -----
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-read-more]');
    if (!btn) return;
    const target = document.getElementById(btn.dataset.readMore);
    if (!target) return;
    target.classList.toggle('product__description--collapsed');
    const expanded = !target.classList.contains('product__description--collapsed');
    btn.textContent = expanded ? (btn.dataset.lessText || 'Read less') : (btn.dataset.moreText || 'Read more');
  });

  // ----- Filter form auto-submit -----
  $$('[data-filters-form]').forEach((form) => {
    let filterTimer;
    const submit = () => {
      clearTimeout(filterTimer);
      filterTimer = setTimeout(() => {
        const formParams = new URLSearchParams(new FormData(form));
        const existing = new URLSearchParams(window.location.search);
        const sortBy = existing.get('sort_by');
        if (sortBy && !formParams.has('sort_by')) formParams.set('sort_by', sortBy);
        formParams.delete('page');
        const url = window.location.pathname + '?' + formParams.toString();
        window.location.href = url;
      }, 300);
    };
    form.addEventListener('change', (e) => {
      if (e.target && e.target.type === 'number') return;
      submit();
    });
    form.addEventListener('input', (e) => {
      if (e.target && e.target.type === 'number') submit();
    });
  });

  // ----- Wishlist / Favorites (localStorage-backed, no app required) -----
  const Wishlist = {
    key: 'caprahem:wishlist',
    list() {
      try { return JSON.parse(localStorage.getItem(this.key)) || []; }
      catch (e) { return []; }
    },
    save(items) {
      try { localStorage.setItem(this.key, JSON.stringify(items)); } catch (e) {}
      this.updateCount();
    },
    has(handle) { return this.list().some((i) => i.handle === handle); },
    add(item) {
      const items = this.list();
      if (!items.some((i) => i.handle === item.handle)) { items.push(item); this.save(items); }
    },
    remove(handle) { this.save(this.list().filter((i) => i.handle !== handle)); },
    toggle(item) {
      if (this.has(item.handle)) { this.remove(item.handle); return false; }
      this.add(item); return true;
    },
    count() { return this.list().length; },
    updateCount() {
      const n = this.count();
      $$('[data-wishlist-count]').forEach((el) => {
        el.textContent = n;
        el.toggleAttribute('hidden', n === 0);
      });
    },
    syncButtons(root) {
      $$('[data-wishlist-toggle]', root || document).forEach((btn) => {
        const active = this.has(btn.dataset.productHandle);
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    },
  };
  window.CaprahemWishlist = Wishlist;

  const WishlistPage = {
    checkEmpty(page) {
      const grid = $('[data-wishlist-grid]', page);
      const empty = $('[data-wishlist-empty]', page);
      const hasCards = grid && grid.querySelector('.card');
      if (grid) grid.toggleAttribute('hidden', !hasCards);
      if (empty) empty.toggleAttribute('hidden', !!hasCards);
    },
    async render(page) {
      if (!page) return;
      const grid = $('[data-wishlist-grid]', page);
      const loading = $('[data-wishlist-loading]', page);
      const empty = $('[data-wishlist-empty]', page);
      // Reset so this is safe to call repeatedly (e.g. each time the drawer opens)
      if (grid) { grid.innerHTML = ''; grid.setAttribute('hidden', ''); }
      if (loading) loading.hidden = false;
      if (empty) empty.hidden = true;
      const items = Wishlist.list();
      if (!items.length) {
        if (loading) loading.hidden = true;
        if (empty) empty.hidden = false;
        return;
      }
      const results = await Promise.all(items.map(async (item) => {
        try {
          const base = item.url || ('/products/' + item.handle);
          const res = await fetch(base + (base.includes('?') ? '&' : '?') + 'view=card', {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          return { item: item, status: res.status, html: res.ok ? await res.text() : null };
        } catch (e) { return { item: item, status: 0, html: null }; }
      }));
      if (loading) loading.hidden = true;
      const dead = []; // only products that DEFINITELY no longer exist (404/410)
      results.forEach((r) => {
        if (r.html && grid) {
          const tmp = document.createElement('div');
          tmp.innerHTML = r.html.trim();
          const card = tmp.querySelector('.card');
          if (card) { grid.appendChild(card); return; }
        }
        if (r.status === 404 || r.status === 410) dead.push(r.item.handle);
      });
      // Prune ONLY dead products, reconciling against current storage so a concurrent
      // add/remove during the fetch window is never clobbered. Never save on network errors.
      if (dead.length) {
        Wishlist.save(Wishlist.list().filter((i) => dead.indexOf(i.handle) === -1));
      } else {
        Wishlist.updateCount();
      }
      Wishlist.syncButtons(grid || document);
      this.checkEmpty(page);
    },
  };

  // Toggle a favorite (event delegation so it works on any rendered card)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    const item = {
      id: btn.dataset.productId,
      handle: btn.dataset.productHandle,
      url: btn.dataset.productUrl,
    };
    if (!item.handle) return;
    const nowActive = Wishlist.toggle(item);
    $$('[data-wishlist-toggle]').forEach((b) => {
      if (b.dataset.productHandle === item.handle) {
        b.classList.toggle('is-active', nowActive);
        b.setAttribute('aria-pressed', nowActive ? 'true' : 'false');
      }
    });
    if (!nowActive) {
      const page = btn.closest('[data-wishlist-page]');
      if (page) {
        const card = btn.closest('.card');
        if (card) card.remove();
        WishlistPage.checkEmpty(page);
      }
    }
  });

  // Sync across browser tabs
  window.addEventListener('storage', (e) => {
    if (e.key === Wishlist.key) { Wishlist.updateCount(); Wishlist.syncButtons(); }
  });

  Wishlist.updateCount();
  Wishlist.syncButtons();
  // Expose a render hook so the wishlist drawer can refresh each time it opens
  window.CaprahemWishlistRender = function () {
    const drawerPage = document.querySelector('#wishlist-drawer [data-wishlist-page]');
    if (drawerPage) WishlistPage.render(drawerPage);
  };
  // Render standalone wishlist pages at load; drawers render on open instead
  $$('[data-wishlist-page]').forEach((page) => {
    if (page.closest('[data-drawer]')) return;
    WishlistPage.render(page);
  });


  // ----- Horizontal product scroller -----
  // The row scrolls on its own; this only adds arrows for pointer users and keeps
  // them in step with the scroll position. A row that already fits shows none.
  function initScroller(root) {
    const track = root.querySelector('[data-scroller-track]');
    const prev = root.querySelector('[data-scroller-prev]');
    const next = root.querySelector('[data-scroller-next]');
    if (!track || !prev || !next) return;

    const overflows = () => track.scrollWidth - track.clientWidth > 4;

    const sync = () => {
      if (!overflows()) {
        prev.hidden = true;
        next.hidden = true;
        return;
      }
      prev.hidden = false;
      next.hidden = false;
      // A fractional scrollLeft can sit a pixel short of either end.
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    };

    // Step by one card, so the snap points always line up.
    const step = () => {
      const item = track.querySelector('.scroller__item');
      if (!item) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
      return item.getBoundingClientRect().width + gap;
    };

    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: prefersReducedMotion() ? 'auto' : 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: prefersReducedMotion() ? 'auto' : 'smooth' }));
    track.addEventListener('scroll', () => window.requestAnimationFrame(sync), { passive: true });
    window.addEventListener('resize', sync);
    if ('ResizeObserver' in window) new ResizeObserver(sync).observe(track);
    sync();
  }

  $$('[data-scroller]').forEach(initScroller);

})();
